import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { MetricCard } from "../ui/index.jsx";
import api from "../../config/api";
import { getErrorMessage } from "../../config/errorHelper";

// ══════════════════════════════════════════════════════
//  Onglet Tickets — métriques et graphiques de l'ancien tableau de bord.
//  Les données ne sont chargées qu'à la première ouverture de l'onglet
//  (le composant n'est monté que lorsqu'il est affiché).
// ══════════════════════════════════════════════════════

const STATUS_COLORS = {
  OPEN: "#00C2CB", IN_PROGRESS: "#7C3AED", PENDING: "#F59E0B", RESOLVED: "#10B981", CLOSED: "#6B7280",
};
const STATUS_LABELS = {
  OPEN: "Ouverts", IN_PROGRESS: "En cours", PENDING: "En attente", RESOLVED: "Résolus", CLOSED: "Fermés",
};

function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--navy-mid)", border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", fontSize: 12 }}>
      <div style={{ color: "var(--text-2)", marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name} : {p.value}</div>)}
    </div>
  );
}

export default function TicketsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/dashboard/admin");
        if (!cancelled) setData(res.data?.data ?? res.data);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div style={{ textAlign: "center", padding: 60, color: "var(--text-3)" }}>
      <i className="bi bi-arrow-repeat spin" style={{ fontSize: 26, display: "block", marginBottom: 10 }} />Chargement des statistiques de tickets…
    </div>
  );
  if (error || !data) return (
    <div style={{ background: "var(--danger-dim)", border: "1px solid var(--danger)", borderRadius: 10, padding: 16, color: "var(--danger)" }}>
      <i className="bi bi-exclamation-triangle me-2" />{error || "Données indisponibles."}
    </div>
  );

  const { metrics, statusCounts, weeklyData } = data;
  const pieData = Object.entries(statusCounts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, color: STATUS_COLORS[k] || "#888" }));
  const criticalCount = metrics?.ticketsCritical ?? 0;

  return (
    <div>
      <div className="dash-metrics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 14, marginBottom: 20 }}>
        <MetricCard icon="bi-ticket-detailed" color="teal" label="Tickets ouverts" value={metrics?.ticketsOpen ?? 0} sub="toutes entreprises" delay={.05} />
        <MetricCard icon="bi-exclamation-triangle" color="orange" label="Tickets critiques" value={criticalCount}
          trendType={criticalCount > 0 ? "down" : "up"} sub="ouverts en ce moment" delay={.1} />
        <MetricCard icon="bi-check2-circle" color="purple" label="Résolus ce mois" value={metrics?.ticketsResolvedThisMonth ?? 0} sub="toutes entreprises" delay={.15} />
        <MetricCard icon="bi-pc-display-horizontal" color="green" label="Assets supervisés" value={metrics?.totalAssets ?? 0}
          trend={`${metrics?.assetsOnlinePercent ?? 0}%`} trendType="up" sub="en ligne" delay={.2} />
      </div>

      <div className="dash-charts-grid" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
        <div className="itm-card anim-fade-up delay-5">
          <div className="card-title-row">
            <div>
              <div className="card-title">Volume de tickets</div>
              <div className="card-sub">Par semaine — 8 dernières semaines</div>
            </div>
            <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "var(--accent-dim)", color: "var(--accent)", border: "1px solid rgba(0,194,203,.2)" }}>Tous clients</span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={weeklyData || []} barSize={7} barGap={3}>
              <XAxis dataKey="label" tick={{ fill: "var(--text-3)", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(255,255,255,.03)" }} />
              <Bar dataKey="created" name="Créés" fill="#00C2CB" radius={[4, 4, 0, 0]} />
              <Bar dataKey="resolved" name="Résolus" fill="#7C3AED" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
            {[["#00C2CB", "Créés"], ["#7C3AED", "Résolus"]].map(([c, l]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-2)" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />{l}
              </div>
            ))}
          </div>
        </div>

        <div className="itm-card anim-fade-up" style={{ animationDelay: ".3s" }}>
          <div className="card-title-row">
            <div>
              <div className="card-title">Statuts</div>
              <div className="card-sub">Répartition tickets actifs</div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <PieChart width={180} height={180}>
              <Pie data={pieData} cx={90} cy={90} innerRadius={55} outerRadius={78} paddingAngle={3} dataKey="value" startAngle={90} endAngle={-270}>
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
            </PieChart>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pieData.map(d => (
              <div key={d.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-2)" }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color, flexShrink: 0 }} />{d.name}
                </div>
                <div style={{ fontFamily: "Syne,sans-serif", fontWeight: 700, color: "var(--text-1)" }}>{d.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}